/**
 * Package D · WebHID USB shipping-scale hook.
 *
 * Supports the vast majority of USB-HID shipping scales: Dymo M25 /
 * M10 / M5, Fairbanks Ultegra, Mettler-Toledo BC, Brecknell 7010,
 * etc. All of them expose a 6-byte HID input report with a fixed
 * layout — vendor differences are limited to the vendor/product ID
 * pair, so we filter loosely and let the browser prompt the user for
 * pairing consent.
 *
 * Report layout (WebHID pushes 5 bytes of data since ReportID is
 * stripped by the browser):
 *   byte 0 : status         02=zero 03=weighing 04=neg 05=over 06=cal 07=fault
 *   byte 1 : unit           0B=ounces 0C=grams 02=lbs-decimal 11=lbs-int
 *   byte 2 : scaling (int8) — 10^exp multiplier for the raw weight
 *   byte 3-4 : weight       little-endian uint16
 *
 * Behaviour
 * ─────────
 * • `available` reflects browser support (Chrome/Edge on secure origin).
 * • `pair()` triggers the browser's device chooser and returns true
 *   when the operator selects a scale.
 * • `weight_lb` streams live once paired; `stable` flips true when
 *   the reading has held ±0.01 lb for 500 ms.
 * • `disconnect()` closes the HID channel and clears state.
 *
 * WebHID keeps device permissions across sessions once granted, so on
 * subsequent visits we auto-reopen the previously-paired scale
 * without prompting again.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const SCALE_FILTERS = [
  { vendorId: 0x0922 },  // Dymo (most common — M25, M10, M5, S250)
  { vendorId: 0x0b67 },  // Fairbanks
  { vendorId: 0x0eb8 },  // Mettler-Toledo
  { vendorId: 0x2474 },  // Salter Brecknell
  { vendorId: 0x1446 },  // Stamps.com scale
  { usagePage: 0x008D }, // HID Scale usage page (spec-compliant devices)
];

const STATUS_ZERO      = 0x02;
const STATUS_WEIGHING  = 0x03;
const STATUS_NEGATIVE  = 0x04;
const STATUS_OVER      = 0x05;
const STATUS_CAL       = 0x06;
const STATUS_FAULT     = 0x07;

// Silence "unused constant" warning — kept as documentation of the
// scale protocol even though the current parser doesn't branch on
// STATUS_WEIGHING (it falls through the default weight-read path).
void STATUS_WEIGHING;

const UNIT_OUNCES         = 0x0B;
const UNIT_GRAMS          = 0x0C;
const UNIT_POUNDS_DECIMAL = 0x02;
const UNIT_KILOGRAMS      = 0x03;

const OZ_PER_LB = 16;
const G_PER_LB  = 453.59237;
const KG_PER_LB = 2.2046226218;

function bytesToWeightLb(bytes) {
  const status  = bytes[0];
  const unit    = bytes[1];
  const scaling = new Int8Array([bytes[2]])[0];
  const raw     = bytes[3] | (bytes[4] << 8);
  const scale   = Math.pow(10, scaling);
  const magnitude = raw * scale;

  const signed = status === STATUS_NEGATIVE ? -magnitude : magnitude;
  switch (unit) {
    case UNIT_OUNCES:         return signed / OZ_PER_LB;
    case UNIT_GRAMS:          return signed / G_PER_LB;
    case UNIT_KILOGRAMS:      return signed * KG_PER_LB;
    case UNIT_POUNDS_DECIMAL:
    case 0x0D: // Fairbanks reports pounds as 0x0D on some models
      return signed;
    default:                  return signed;   // best-effort — treat as pounds
  }
}

// Exposed for unit tests only.
export const __TEST__ = { bytesToWeightLb };

export function useUsbScale() {
  const [available, setAvailable] = useState(false);
  const [device,    setDevice]    = useState(null);
  const [weightLb,  setWeightLb]  = useState(null);
  const [status,    setStatus]    = useState("disconnected");   // disconnected|paired|reading|error
  const [rawStatus, setRawStatus] = useState(null);             // last HID status byte
  const [stable,    setStable]    = useState(false);
  const [error,     setError]     = useState(null);

  const stableTimerRef = useRef(null);
  const lastWeightRef  = useRef(null);

  // Browser support: WebHID lives on `navigator.hid`. Chrome / Edge only.
  useEffect(() => {
    setAvailable(typeof navigator !== "undefined" && "hid" in navigator);
  }, []);

  const handleReport = useCallback((event) => {
    try {
      const b = new Uint8Array(event.data.buffer);
      if (b.length < 5) return;
      const st = b[0];
      setRawStatus(st);
      if (st === STATUS_FAULT || st === STATUS_CAL) {
        setError(st === STATUS_FAULT ? "Scale fault" : "Scale needs calibration");
        setStable(false);
        return;
      }
      if (st === STATUS_OVER) {
        setError("Overweight");
        setStable(false);
        return;
      }
      setError(null);
      const lb = bytesToWeightLb(b);
      const rounded = Math.max(0, Math.round(lb * 100) / 100);
      setWeightLb(rounded);
      setStatus(st === STATUS_ZERO ? "paired" : "reading");

      // Stability window: 500 ms of ±0.01 lb variance.
      if (lastWeightRef.current !== null && Math.abs(rounded - lastWeightRef.current) < 0.01) {
        if (!stableTimerRef.current) {
          stableTimerRef.current = setTimeout(() => setStable(true), 500);
        }
      } else {
        setStable(false);
        if (stableTimerRef.current) {
          clearTimeout(stableTimerRef.current);
          stableTimerRef.current = null;
        }
      }
      lastWeightRef.current = rounded;
    } catch (err) {
      setError(err.message || "Scale read failed");
    }
  }, []);

  const attachDevice = useCallback(async (d) => {
    try {
      if (!d.opened) await d.open();
      d.addEventListener("inputreport", handleReport);
      setDevice(d);
      setStatus("paired");
      setError(null);
    } catch (err) {
      setStatus("error");
      setError(err.message || "Could not open scale");
    }
  }, [handleReport]);

  // Auto-reconnect on mount if we've paired with a scale before.
  useEffect(() => {
    if (!available) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await navigator.hid.getDevices();
        if (cancelled) return;
        const match = list.find(d => SCALE_FILTERS.some(f =>
          (f.vendorId && d.vendorId === f.vendorId)
        ));
        if (match) await attachDevice(match);
      } catch {/* silent */}
    })();
    return () => { cancelled = true; };
  }, [available, attachDevice]);

  const pair = useCallback(async () => {
    if (!available) {
      setError("WebHID not supported — use Chrome or Edge");
      return false;
    }
    try {
      const [d] = await navigator.hid.requestDevice({ filters: SCALE_FILTERS });
      if (!d) return false;
      await attachDevice(d);
      return true;
    } catch (err) {
      setError(err.message || "Pairing cancelled");
      return false;
    }
  }, [available, attachDevice]);

  const disconnect = useCallback(async () => {
    try {
      if (device) {
        device.removeEventListener("inputreport", handleReport);
        if (device.opened) await device.close();
      }
    } catch {/* silent */}
    setDevice(null);
    setStatus("disconnected");
    setWeightLb(null);
    setStable(false);
    setError(null);
    lastWeightRef.current = null;
    if (stableTimerRef.current) { clearTimeout(stableTimerRef.current); stableTimerRef.current = null; }
  }, [device, handleReport]);

  return {
    available,
    paired:      status !== "disconnected" && status !== "error",
    reading:     status === "reading",
    stable,
    status,
    rawStatus,
    weightLb,
    error,
    deviceName:  device?.productName || null,
    pair,
    disconnect,
  };
}
